'use client';

import { cn } from '@core/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactElement } from 'react';
import React, { createContext, useContext, useRef } from 'react';

import { useIsomorphicLayoutEffect } from '../../../hooks';

type TabContextType<T extends string = string> = {
  activeValue: T;
  onChange?: (value: T) => void;
  size?: 'sm' | 'lg';
  variant?: 'full' | 'flex';
};

const TabContext = createContext<TabContextType<string> | undefined>(undefined);

const useTabContext = <T extends string = string>() => {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabContext must be used within a TabProvider');
  }

  return context as unknown as TabContextType<T>;
};

const tabItemVariants = cva(
  'relative flex shrink-0 items-center justify-center gap-4 whitespace-nowrap bg-transparent outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-Gray-black/50',
  {
    variants: {
      size: {
        sm: 'h-53 typo-body4',
        lg: 'h-70 typo-body1',
      },
      variant: {
        full: 'flex-1',
        flex: 'px-24',
      },
      active: {
        true: 'font-700 text-Gray-black',
        false:
          'font-400 text-Gray-900 hover:text-Gray-600 active:text-Gray-500',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
        false: 'cursor-pointer',
      },
    },
    compoundVariants: [
      { variant: 'full', size: 'sm', className: 'px-16' },
      { variant: 'full', size: 'lg', className: 'px-20' },
    ],
    defaultVariants: {
      size: 'sm',
      variant: 'full',
      active: false,
      disabled: false,
    },
  },
);

interface TabItemProps extends VariantProps<typeof tabItemVariants> {
  value: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

function TabItem({
  value,
  icon,
  disabled = false,
  className,
  children,
}: TabItemProps) {
  const { activeValue, onChange, size, variant } = useTabContext();
  const isActive = activeValue === value;

  const handleClick = () => {
    if (!disabled) {
      onChange?.(value);
    }
  };

  return (
    <button
      type="button"
      className={cn(
        tabItemVariants({
          size,
          variant,
          active: isActive,
          disabled,
          className,
        }),
      )}
      onClick={handleClick}
      disabled={disabled}
      aria-selected={isActive}
      role="tab"
      tabIndex={isActive ? 0 : -1}
      data-value={value}
    >
      <span className="flex flex-col items-center">
        {/* 활성(font-700) 폭을 미리 예약해 탭 전환 시 이웃 탭이 밀리지 않게 한다 */}
        <span aria-hidden className="invisible h-0 overflow-hidden font-700">
          {children}
        </span>
        <span>{children}</span>
      </span>
      {icon}
    </button>
  );
}

const tabVariants = cva(
  'relative isolate overflow-x-auto overflow-y-hidden border-b border-Gray-300 hide-scrollbar',
  {
    variants: {
      size: {
        sm: 'h-53',
        lg: 'h-70',
      },
      variant: {
        full: 'flex',
        flex: 'flex',
      },
    },
    defaultVariants: {
      size: 'sm',
      variant: 'full',
    },
  },
);

interface TabProps<T extends string = string>
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof tabVariants> {
  value: T;
  onChange?: (value: T) => void;
  size?: 'sm' | 'lg';
  variant?: 'full' | 'flex';
  children: React.ReactNode;
  className?: string;
}

type TabComponent = {
  <T extends string = string>(props: TabProps<T>): ReactElement;
  Item: <V extends string>(
    props: Omit<TabItemProps, 'value'> & { value: V },
  ) => ReactElement;
};

function TabBase<T extends string = string>({
  value,
  onChange,
  size = 'sm',
  variant = 'full',
  children,
  className,
  ...props
}: TabProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const previousValueRef = useRef(value);

  const handleChange = (newValue: string) => {
    onChange?.(newValue as T);
  };

  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current;
    const indicator = indicatorRef.current;
    if (!container || !indicator) {
      return;
    }

    const activeItem = container.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]',
    );

    const applyIndicatorPosition = (shouldAnimate: boolean) => {
      if (!activeItem) {
        indicator.getAnimations().forEach((animation) => animation.cancel());
        indicator.style.width = '0px';

        return;
      }

      const nextTransform = `translateX(${activeItem.offsetLeft}px)`;
      const nextWidth = `${activeItem.offsetWidth}px`;

      // ResizeObserver는 observe 직후 1회 즉시 발화하므로, 목표가 그대로면 스킵해
      // 진행 중인 슬라이드 애니메이션이 cancel되지 않게 한다
      if (
        indicator.style.transform === nextTransform &&
        indicator.style.width === nextWidth
      ) {
        return;
      }

      // 연속 전환 시 현재 시각 위치에서 이어 슬라이드하도록 cancel 전에 계산 스타일을 읽는다
      const computedStyle = getComputedStyle(indicator);
      const fromTransform = computedStyle.transform;
      const fromWidth = computedStyle.width;

      indicator.getAnimations().forEach((animation) => animation.cancel());
      indicator.style.transform = nextTransform;
      indicator.style.width = nextWidth;

      if (
        shouldAnimate &&
        fromWidth !== '0px' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        indicator.animate(
          [
            { transform: fromTransform, width: fromWidth },
            { transform: nextTransform, width: nextWidth },
          ],
          { duration: 200, easing: 'ease-out' },
        );
      }
    };

    const shouldAnimate = previousValueRef.current !== value;

    applyIndicatorPosition(shouldAnimate);

    if (shouldAnimate && activeItem) {
      const itemStart = activeItem.offsetLeft;
      const itemEnd = itemStart + activeItem.offsetWidth;
      const visibleStart = container.scrollLeft;
      const visibleEnd = visibleStart + container.clientWidth;
      const scrollMargin = 16;

      if (itemStart < visibleStart) {
        container.scrollLeft = Math.max(itemStart - scrollMargin, 0);
      } else if (itemEnd > visibleEnd) {
        container.scrollLeft = itemEnd - container.clientWidth + scrollMargin;
      }
    }

    previousValueRef.current = value;

    const resizeObserver = new ResizeObserver(() => {
      applyIndicatorPosition(false);
    });

    resizeObserver.observe(container);
    if (activeItem) {
      resizeObserver.observe(activeItem);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [value, size, variant, children]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const tabs = Array.from(
      container.querySelectorAll<HTMLButtonElement>(
        '[role="tab"]:not(:disabled)',
      ),
    );

    if (tabs.length === 0) {
      return;
    }

    const currentIndex = tabs.findIndex(
      (tab) => tab === document.activeElement,
    );

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex =
          currentIndex < 0
            ? tabs.length - 1
            : (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTab = tabs[nextIndex];
    nextTab.focus();

    const nextValue = nextTab.dataset.value;
    if (nextValue !== undefined) {
      handleChange(nextValue);
    }
  };

  return (
    <TabContext.Provider
      value={{
        activeValue: value,
        onChange: handleChange,
        size,
        variant,
      }}
    >
      <div
        ref={containerRef}
        className={cn(
          tabVariants({
            size,
            variant,
            className,
          }),
        )}
        role="tablist"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        {...props}
      >
        <span
          ref={indicatorRef}
          aria-hidden
          className={cn(
            'pointer-events-none absolute bottom-0 left-0 z-10 bg-Gray-black',
            {
              'h-2': size === 'sm',
              'h-4': size === 'lg',
            },
          )}
        />
        {children}
      </div>
    </TabContext.Provider>
  );
}

export const Tab = TabBase as unknown as TabComponent;
Tab.Item = TabItem;

export type { TabComponent, TabContextType, TabItemProps, TabProps };
export { useTabContext };
